import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clock, FolderTree, Receipt, Split } from "lucide-react";
import { z } from "zod";
import {
  businessDate,
  hours,
  money,
  seesMoney,
  type ContractorInvoiceRow,
  type MoneyByCurrency,
  type ProjectInvoiceSummary,
  type ProjectRow,
} from "@contractorhub/shared";
import {
  Alert,
  Avatar,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  StatCard,
  type Column,
} from "../../../components/ui";
import { Amount, AmountByCurrency } from "../../../components/invoices/invoice-display";
import { InvoiceStatusBadge } from "../../../components/invoices/invoice-badges";
import {
  BudgetBurn,
  CodeChip,
  ProjectTimeline,
  projectStatusLabel,
} from "../../../components/clients/project-bits";
import { adminExpensesQuery, adminInvoicesQuery, adminProjectDetailQuery } from "../../../lib/queries";
import { RecordExpenseButton } from "../../../components/expenses/record-expense-modal";
import { ExpenseStatusBadge, categoryLabel } from "../../../components/expenses/expense-bits";

const searchSchema = z.object({ page: z.coerce.number().int().min(1).default(1) });

const expensesFor = (projectId: string) =>
  adminExpensesQuery({ projectId, status: "ALL", sort: "expenseDate", dir: "desc" });

const invoicesFor = (projectId: string, page: number) =>
  adminInvoicesQuery({ status: "ALL", projectId, page, sort: "period", dir: "desc" });

export const Route = createFileRoute("/_authed/_staff/projects/$id")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  // A user admin's page has no invoices or expenses (AUTH-13): both routes are
  // admin-only, so they are not even asked for.
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminProjectDetailQuery(params.id)),
      ...(seesMoney(context.user.role)
        ? [
            context.queryClient.ensureQueryData(invoicesFor(params.id, deps.page)),
            context.queryClient.ensureQueryData(expensesFor(params.id)),
          ]
        : []),
    ]),
  component: ProjectPage,
});

/** Several currencies' totals as one figure, or a dash when there are none. */
function sumOf(...lists: MoneyByCurrency[]): MoneyByCurrency {
  const out = new Map<string, number>();
  for (const list of lists) for (const m of list) out.set(m.currency, (out.get(m.currency) ?? 0) + m.value);
  return [...out].map(([currency, value]) => ({ currency, value }) as MoneyByCurrency[number]);
}

/**
 * One project (CINV-20): its timeline and labour budget, what invoice lines
 * filed under it add up to, and those invoices.
 *
 * Two different facts, shown side by side and never added: "Budget used" is
 * approved timesheet hours at their frozen rate; the invoice figures are what
 * contractors invoiced on lines filed here. And an invoice split across
 * projects counts only its share here — its slice, never its total.
 *
 * A user admin sees the project without its money (AUTH-13): the timeline and
 * the approved hours, and none of the budget, invoices or expenses.
 */
function ProjectPage() {
  const { id } = Route.useParams();
  const { user } = Route.useRouteContext();
  const fullAdmin = seesMoney(user.role);
  const { data } = useSuspenseQuery(adminProjectDetailQuery(id));
  const { project, invoices } = data;
  const today = businessDate();

  return (
    <div>
      <Link
        to="/clients"
        search={{ view: "projects" }}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Clients & Projects
      </Link>
      <PageHeader
        eyebrow={project.clientName}
        title={
          <span className="flex flex-wrap items-center gap-2">
            {project.name} <CodeChip code={project.code} />
          </span>
        }
        description={`${projectStatusLabel(project.status)} · ${project.contractorCount} contractor${project.contractorCount === 1 ? "" : "s"}`}
        actions={fullAdmin ? <RecordExpenseButton projectId={project.id} /> : undefined}
      />

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card className="p-5">
          <p className="mb-2 text-sm font-medium text-muted">Timeline</p>
          <ProjectTimeline project={project} today={today} />
        </Card>
        {fullAdmin ? (
          <Card className="p-5">
            <p className="mb-2 text-sm font-medium text-muted">Budget used — approved timesheets</p>
            <BudgetBurn
              budget={project.budget ?? null}
              spend={project.approvedSpend ?? 0}
              unpricedHours={project.unpricedHours ?? 0}
            />
          </Card>
        ) : (
          <Card className="p-5">
            <p className="mb-2 text-sm font-medium text-muted">Approved timesheet hours</p>
            <p className="text-2xl font-semibold tabular-nums text-text">
              {hours(project.approvedHours)} h
            </p>
          </Card>
        )}
      </div>

      {fullAdmin && invoices && <ProjectMoney project={project} invoices={invoices} />}
    </div>
  );
}

/** The invoices and expenses filed under a project — a full admin's alone. */
function ProjectMoney({
  project,
  invoices,
}: {
  project: ProjectRow;
  invoices: ProjectInvoiceSummary;
}) {
  const id = project.id;
  const { page } = Route.useSearch();
  const navigate = useNavigate();
  const { data: list } = useSuspenseQuery(invoicesFor(id, page));
  const { data: expenses } = useSuspenseQuery(expensesFor(id));
  const spent = expenses.summary.spent;
  const open = (row: ContractorInvoiceRow) =>
    void navigate({ to: "/invoices/$id", params: { id: row.id } });
  const invoiced = sumOf(invoices.amounts.submitted, invoices.amounts.approved, invoices.amounts.paid);

  const columns: Column<ContractorInvoiceRow>[] = [
    {
      key: "contractor",
      header: "Contractor",
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={row.contractorName} />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              open(row);
            }}
            className="truncate rounded-sm text-left font-medium text-text hover:text-accent"
          >
            {row.contractorName}
          </button>
        </div>
      ),
    },
    {
      key: "invoice",
      header: "Invoice",
      cell: (row) => (
        <div className="whitespace-nowrap">
          <div className="text-text">{row.invoiceNumber ?? <span className="text-subtle">No number</span>}</div>
          <div className="text-xs text-subtle">{row.periodLabel}</div>
        </div>
      ),
    },
    {
      key: "share",
      header: "On this project",
      align: "right",
      cell: (row) => {
        const slice = row.projects.find((p) => p.projectId === id);
        if (!slice) return <span className="text-subtle">—</span>;
        const totalLines = row.projects.reduce((n, p) => n + p.lineCount, 0) + (row.untaggedLineCount ?? 0);
        return (
          <div className="whitespace-nowrap tabular-nums">
            <div className="font-semibold text-text">
              {slice.amount === null ? (
                <span className="font-normal text-subtle">Amount unknown</span>
              ) : (
                money(slice.amount, row.currency)
              )}
            </div>
            <div className="text-xs text-subtle">
              {slice.hours !== null && `${hours(slice.hours)} h · `}
              {/* No lines read, filed whole under this project (CINV-35). */}
              {slice.lineCount === 0
                ? "Whole invoice"
                : `${slice.lineCount} of ${totalLines} line${totalLines === 1 ? "" : "s"}`}
              {row.projects.length > 1 && ` · split across ${row.projects.length}`}
            </div>
          </div>
        );
      },
    },
    {
      key: "total",
      header: "Invoice total",
      align: "right",
      priority: "lg",
      cell: (row) => <Amount value={row.total} currency={row.currency} className="text-muted" />,
    },
    {
      key: "status",
      header: "Status",
      priority: "sm",
      cell: (row) => <InvoiceStatusBadge status={row.status} />,
    },
    {
      key: "open",
      header: <span className="sr-only">Open</span>,
      align: "right",
      width: "w-10",
      cell: (row) => (
        <button
          type="button"
          aria-label={`Open ${row.contractorName}, ${row.periodLabel}`}
          onClick={(event) => {
            event.stopPropagation();
            open(row);
          }}
          className="grid h-8 w-8 place-items-center rounded-md text-subtle transition-colors hover:bg-surface-hover hover:text-text"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Invoices filed here" value={invoices.invoices} icon={Receipt} />
        <StatCard
          label="Invoiced on this project"
          value={invoiced.length ? <AmountByCurrency value={invoiced} /> : "—"}
          icon={FolderTree}
          hint="This project's share only, never a split invoice's whole total"
        />
        <StatCard
          label="Hours invoiced here"
          value={invoices.hours === null ? "Some unknown" : hours(invoices.hours)}
          icon={Clock}
        />
        <StatCard label="Split across projects" value={invoices.splitCount} icon={Split} />
      </div>
      {invoices.amountsPartial && (
        <Alert tone="warn" className="mb-4">
          Some lines filed here have no amount read, so these figures are a minimum.
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Invoices"
          description="Every contractor invoice filed under this project — by its lines, or as a whole. File one from its own page, or several from Invoices."
        />
        <DataTable
          caption={`Invoices for ${project.name}`}
          columns={columns}
          rows={list.rows}
          getRowKey={(row) => row.id}
          onRowClick={open}
          empty={
            <EmptyState
              icon={Receipt}
              title="No invoices filed here yet"
              description="An invoice appears once one of its lines is filed under this project, or an admin files the whole invoice under it."
            />
          }
        />
        <Pagination
          page={list.pageInfo.page}
          pageSize={list.pageInfo.pageSize}
          total={list.pageInfo.total}
          onPageChange={(next) => void navigate({ to: "/projects/$id", params: { id }, search: { page: next } })}
        />
      </Card>

      <Card className="mt-6">
        <CardHeader
          title="Expenses"
          description={
            spent.length
              ? "Supplier invoices and receipts recorded against this project — its own spend, beside (never added to) what contractors invoiced."
              : "Supplier invoices and receipts recorded against this project."
          }
          actions={spent.length ? <AmountByCurrency value={spent} className="text-sm font-semibold text-text" /> : null}
        />
        {expenses.rows.length === 0 ? (
          <EmptyState icon={Receipt} title="No expenses recorded" description="Record a supplier invoice or receipt for this project." />
        ) : (
          <ul className="divide-y divide-line">
            {expenses.rows.map((e) => (
              <li key={e.id}>
                <Link
                  to="/expenses/$id"
                  params={{ id: e.id }}
                  className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm hover:bg-surface-hover"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-text">{e.supplierName ?? "Vendor not read yet"}</div>
                    <div className="truncate text-xs text-subtle">
                      {e.expenseDate} · {categoryLabel(e.category)}
                      {e.description ? ` · ${e.description}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <Amount value={e.total} currency={e.currency} className="font-semibold text-text" />
                    <ExpenseStatusBadge status={e.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {expenses.pageInfo.total > expenses.rows.length && (
          <div className="px-5 py-3 text-sm">
            <Link to="/expenses" search={{ projectId: project.id }} className="text-accent hover:underline">
              All {expenses.pageInfo.total} expenses for this project
            </Link>
          </div>
        )}
      </Card>
    </>
  );
}
